import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, InputAdornment, Menu, MenuItem, TextField, Tooltip, useTheme } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ClearIcon from '@mui/icons-material/Clear';
import { Key, useContext, useEffect, useState } from "react";
import "./RoleNames.css"
import CreateRoleDialog from "./CreateRoleDialog";
import { toast } from "react-toastify";
import axiosInstance from "../../../../axios/axios";
import { END_POINTS } from "../../../../shared";
import RenameDialog from "./RenameRole";
import DeleteDialog from "./DeleteRole";
import { ThemeContext } from "../../../../ThemeContext";
import { useRoleContext } from "../RoleContext";
import useRolePermissions from "../../../../shared/hooks/useRolePermissions";
import { ROLES_CONTENT_PROPS } from "../../../../shared/constants/constant-data";

const RoleNames = ({ Roles,setRoles, OnRoleClick, initialSelectedRole, setLoading, checkUnsavedChanges, }: any) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRole, setSelectedRole] = useState<any>(null);
  const [onSelectedRole, onSetSelectedRole] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [openDialog, setOpenDialog] = useState(false);
  const [openRenameDialog, setOpenRenameDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false); 
  const [pendingRole, setPendingRole] = useState<any>(null); 
  const { triggerSaveChanges } = useRoleContext();
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const { hasPermissions } = useRolePermissions();


  const handleRename = async (new_role:any,newName: string) => {
    console.log("Renaming role:", { selectedRole, newName });
    console.log("Renaming-new role:", new_role);
    setLoading(true); 
    try {
      const payload = {
        "role_id": new_role.id,
        "role_name": newName
      }
      const response = await axiosInstance.post(`${END_POINTS.ROLE_NAME}`, payload);
      if (response.status === 200) {
        toast(response.data.message, { type: "success" });
        console.log("Previous Roles:", Roles);
        setRoles((prevRoles: any[]) => {
          const index = prevRoles.findIndex((role:any) => role.id === new_role.id);
          if (index === -1) return prevRoles; 
          const updatedRoles = [...prevRoles];
          updatedRoles[index] = { ...updatedRoles[index], name: newName };
          if (selectedRole?.id === new_role.id) {
            setSelectedRole(updatedRoles[index]);
            OnRoleClick(updatedRoles[index]);
          }
          return updatedRoles;
        });
        console.log("Updated Roles:", Roles);
        setOpenRenameDialog(false);
      }
    } catch (error: any) {
      console.error("Error response:", error.response);
      toast.error(
        error.response?.data?.message || "Something went wrong",
        { type: "error" }
      );
    }
    finally {
      setLoading(false); 
    }
  };
  const handleDelete = async (role:any) => {
    console.log("Delete role:", { selectedRole });
    setLoading(true); 
    try {
      const payload = {
        "role_id": role?.id,
      }
      const response = await axiosInstance.post(`${END_POINTS.DELETE_ROLE}`, payload);
      if (response.status === 200) {
        toast(response.data.message, { type: "success" });
        setRoles((prevRoles: any[]) => {
          const updatedRoles = prevRoles.filter((r) => r.id !== role.id);
          if (updatedRoles.length > 0) {
            const firstRole = updatedRoles[0];
            onSetSelectedRole(firstRole.name); 
            OnRoleClick(firstRole); 
          } else {
            onSetSelectedRole(null); 
            OnRoleClick(null);
          }
          return updatedRoles;
        });
      }
    } catch (error: any) {
      console.error("Error response:", error.response);
      toast.error(
        error.response?.data?.message || "Something went wrong",
        { type: "error" }
      );
    }
    finally {
      setLoading(false); 
    }
  }

  const handleAddClick = () => {
    setOpenDialog(true);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>,role:any) => {
    setAnchorEl(event.currentTarget);
    setSelectedRole(role); // Set the selected role based on the clicked element
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (action: string) => {
    console.log(selectedRole, action);
    if (action === "rename") {
      setOpenRenameDialog(true); // Open RenameDialog
    } else if (action === "delete") {
      setOpenDeleteDialog(true); // Open DeleteDialog
    }
    handleClose(); // Close the menu
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const filteredRoles = Roles.filter((role: any) =>
    role.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateRole = async (roleName: string, cloneRole: string) => {
    console.log("Creating role:", { roleName, cloneRole });
    setLoading(true); 
    try {
      const roleReq: any = {
        "clone_role_id": cloneRole,
        "role_name": roleName,
      };
      console.log("Payload being sent:", roleReq);

      const response = await axiosInstance.post(END_POINTS.SAVE_USERS, roleReq);
      if (response.status === 200) {
        toast(response.data.message, { type: "success" });
        const newRole = {
          name: roleName,
          status: true,
          id: response.data?.data?.role_id,
          default_role: false,
        };
        Roles.push(newRole);
        onSetSelectedRole(roleName);
        OnRoleClick(newRole);
      }
    } catch (error: any) {
      console.error("Error response:", error.response);
      toast.error(
        error.response?.data?.message || "Something went wrong",
        { type: "error" }
      );
    }  finally {
      setLoading(false); 
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const themeContext = useContext(ThemeContext);

  if (!themeContext) {
    throw new Error("ThemeContext must be used within a ThemeProvider");
  }

  const { darkMode } = themeContext;
  
  const onRolesClick = async (role: any) => {
    if (!await checkUnsavedChanges) {
      setPendingRole(role);
      setIsDialogOpen(true);
    } else {
      onSetSelectedRole(role.name);
      OnRoleClick(role);
    }
  };

  const confirmSaveChanges = async () => {
    console.log("Confirming save changes");
    // await saveChanges 
    triggerSaveChanges();
    setIsDialogOpen(false);
    if (pendingRole) {
      OnRoleClick(pendingRole);
      setPendingRole(null);
    }
  };

  const discardChangesAndSwitch = () => {
    setIsDialogOpen(false);
    if (pendingRole) {
      onSetSelectedRole(pendingRole.name);
      OnRoleClick(pendingRole);
      setPendingRole(null);
    }
  };

  // useEffect(() => {
  //   if (Roles.length > 0) {
  //     const updatedSelectedRole = Roles.find((role: any) => role?.id === selectedRole?.id);
  //     if (updatedSelectedRole) {
  //       onSetSelectedRole(updatedSelectedRole.name);
  //       OnRoleClick(updatedSelectedRole);
  //     } else {
  //       onSetSelectedRole(Roles[0].name);
  //       OnRoleClick(Roles[0]);
  //     }
  //   }
  // }, [Roles]);

  useEffect(() => {
    if (initialSelectedRole) {
      onSetSelectedRole(initialSelectedRole.name);
      OnRoleClick(initialSelectedRole);
    }
  }, [initialSelectedRole]);

  return (
    <>
      <div style={{
        height: "49px",
        padding: "12px 6px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: "1px solid #E2E2F2"
      }}>

        <TextField
          placeholder="Search"
          size="small"
          variant="standard"
          fullWidth
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{
            m: 1,
            width: 'calc(100% - 16px)',
            color: theme.palette.text.primary,
            borderRadius: "6px",
            border: "1px solid",
            borderColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)",
            backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
            transition: "all 0.3s ease",
            "&:hover": {
              borderColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.24)" : "rgba(0, 0, 0, 0.24)",
            },
            "&:focus-within": {
              borderColor: isDarkMode ? theme.palette.primary.main : "",
              boxShadow: isDarkMode ? `0 0 0 2px ${theme.palette.primary.main}25` : "",
              backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
            },
            "& .MuiInputBase-root": {
              height: "32px !important",
              minHeight: "32px !important",
              alignItems: "center",
              paddingLeft: "8px",
              paddingRight: "8px",
            },
            "& .MuiInputBase-input": {
              color: theme.palette.text.primary,
              lineHeight: "32px",
              padding: "0px !important",
            },
            "& .MuiInputBase-input::placeholder": {
              color: theme.palette.text.secondary,
              opacity: 0.8,
            },
            "& fieldset": { border: "none" },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery("")} sx={{ mr: 1 }}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
            disableUnderline: true,
          }}
        />
        {hasPermissions(ROLES_CONTENT_PROPS.MODIFY_ROLE_PERMISSION) && (
          <IconButton onClick={handleAddClick}>
          <AddIcon sx={{ color: "blue" }} />
        </IconButton>
        )}

        <CreateRoleDialog
          open={openDialog}
          onClose={handleCloseDialog}
          onCreate={handleCreateRole}
          roles={Roles}
        />

      </div>
      <div className="scroll" style={{
        height: "calc(100vh - 160px)",
      }}>
        {filteredRoles?.map((role: any, index: Key | null | undefined) => (
          <div key={index}
            className={`role-item${onSelectedRole === role.name ? ' selected-role' : ''} ${isDarkMode ? "dark-mode" : "light-mode"}`} 
            onClick={() => onRolesClick(role)}  
            style={{
              width: "100%",
              cursor: "pointer",
              padding: "10px 15px",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              height:'36px'
            }}>
           
              <p title={role.name} style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                margin: "0",
                maxWidth: "80%",
                // color: onSelectedRole === role.name ? "#fff" : darkMode ? "#fff" : "#000", 
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                fontFamily: "Manrope",
                fontSize: "12px",
                fontWeight: 600,
                lineHeight: "20px",
              }}  >{role.name}</p>
            
            {!role.default_role && (
              <>
                {hasPermissions(ROLES_CONTENT_PROPS.MODIFY_ROLE_PERMISSION) && (
                  <IconButton onClick={(e) => {
                   e.stopPropagation();
                  handleClick(e,role)
                }}
                 sx={{
                  color: onSelectedRole === role.name ? "#fff" : "inherit", 

                }}
                  >
                  <MoreVertIcon className="kebab-icon" />
                </IconButton>
                )}
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleClose();
                  }}
                >
            <MenuItem onClick={(e) => {
              e.stopPropagation(); 
              handleMenuItemClick("rename");
            }}>Rename</MenuItem>
                  <MenuItem onClick={(e) => {
            e.stopPropagation(); 
            handleMenuItemClick("delete");
          }}>Delete Role</MenuItem>
                </Menu>
              </>
            )}
          </div>
        ))}
      </div>
      {openRenameDialog && (
        <RenameDialog
          open={openRenameDialog}
          onClose={() => setOpenRenameDialog(false)}
          role={selectedRole}
          onRename={handleRename}
        />
      )}
      {openDeleteDialog && (
        <DeleteDialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          role={selectedRole}
          onDelete={handleDelete}
        />
      )}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        aria-labelledby="save-changes-dialog-title"
        aria-describedby="save-changes-dialog-description"
      >
        <DialogTitle id="save-changes-dialog-title">Save Changes</DialogTitle>
        <DialogContent>
          <DialogContentText id="save-changes-dialog-description">
            You have unsaved changes. Do you want to save them before switching roles?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={discardChangesAndSwitch} color="secondary">
            No
          </Button>
          <Button onClick={confirmSaveChanges} color="primary" variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default RoleNames;